import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconBuilding,
  IconChartBar,
  IconShieldCheckered,
  IconStack3,
  IconTrash,
  IconUserPlus,
} from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import {
  useCompany,
  useCompanyProjects,
  useCompanyRollup,
  useCompanyViewers,
  useGrantCompanyViewer,
  useRevokeCompanyViewer,
} from '../../../queries/companies';
import { useAdminUsersList } from '../../../queries/admin-users';

function fmtBytes(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n) || n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export function CompanyDetailPage() {
  const { id = '' } = useParams();
  const companyQ = useCompany(id);
  const rollupQ = useCompanyRollup(id);
  const projectsQ = useCompanyProjects(id);
  const viewersQ = useCompanyViewers(id);
  const grantM = useGrantCompanyViewer(id);
  const revokeM = useRevokeCompanyViewer(id);

  const [userSearch, setUserSearch] = useState('');
  const usersQ = useAdminUsersList(userSearch ? { q: userSearch, limit: 10 } : { limit: 10 });

  if (companyQ.isLoading) {
    return (
      <Center h="60vh">
        <Loader />
      </Center>
    );
  }

  if (!companyQ.data) {
    return (
      <Container>
        <Text>Company not found.</Text>
      </Container>
    );
  }

  const c = companyQ.data;
  const rollup = rollupQ.data;
  const projects = projectsQ.data ?? [];
  const viewers = viewersQ.data ?? [];
  const isDeleted = c.deleted_at !== null;

  function confirmGrant(userId: string, label: string) {
    modals.openConfirmModal({
      title: 'Grant company-rollup access?',
      children: (
        <Text size="sm">
          {label} will be able to read this company's rollup metrics. They will not gain access to
          any individual project's data.
        </Text>
      ),
      labels: { confirm: 'Grant access', cancel: 'Cancel' },
      onConfirm: () => {
        grantM.mutate(userId, {
          onSuccess: () =>
            notifications.show({ title: 'Viewer granted', message: label, color: 'pine' }),
          onError: (e: Error) =>
            notifications.show({
              title: 'Could not grant viewer',
              message: e.message,
              color: 'red',
            }),
        });
      },
    });
  }

  function confirmRevoke(userId: string, label: string) {
    modals.openConfirmModal({
      title: 'Revoke access?',
      children: <Text size="sm">{label} will lose access to this company's rollup.</Text>,
      labels: { confirm: 'Revoke', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        revokeM.mutate(userId, {
          onSuccess: () =>
            notifications.show({ title: 'Viewer revoked', message: label, color: 'pine' }),
          onError: (e: Error) =>
            notifications.show({
              title: 'Could not revoke viewer',
              message: e.message,
              color: 'red',
            }),
        });
      },
    });
  }

  return (
    <Container size="lg">
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor component={Link} to="/admin">
            Admin
          </Anchor>
          <Anchor component={Link} to="/admin/companies">
            Companies
          </Anchor>
          <Text>{c.name}</Text>
        </Breadcrumbs>

        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <Group gap="xs">
              <IconBuilding size={20} />
              <Title order={2}>{c.name}</Title>
              {isDeleted && (
                <Badge color="red" variant="light">
                  Deleted
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              <code>{c.slug}</code> · created {dayjs(c.created_at).format('YYYY-MM-DD')}
            </Text>
            {c.description && <Text size="sm">{c.description}</Text>}
          </Stack>
        </Group>

        <Tabs defaultValue="rollup">
          <Tabs.List>
            <Tabs.Tab value="rollup" leftSection={<IconChartBar size={14} />}>
              Rollup
            </Tabs.Tab>
            <Tabs.Tab value="projects" leftSection={<IconStack3 size={14} />}>
              Projects
            </Tabs.Tab>
            <Tabs.Tab value="viewers" leftSection={<IconShieldCheckered size={14} />}>
              Viewers ({viewers.length})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="rollup" pt="md">
            {rollupQ.isLoading && <Loader size="sm" />}
            {rollup && (
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
                <RollupCard label="Projects" value={rollup.project_count} />
                <RollupCard label="Tracts" value={rollup.tract_count} />
                <RollupCard label="Title runs" value={rollup.title_run_count} />
                <RollupCard label="Runsheet entries" value={rollup.runsheet_entry_count} />
                <RollupCard label="Open requirements" value={rollup.open_requirements_count} />
                <RollupCard label="Document storage" value={fmtBytes(rollup.documents_total_bytes)} />
                <RollupCard
                  label="Last activity"
                  value={
                    rollup.last_activity_at
                      ? dayjs(rollup.last_activity_at).format('YYYY-MM-DD HH:mm')
                      : '—'
                  }
                />
              </SimpleGrid>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="projects" pt="md">
            <Paper withBorder radius="md">
              <DataTable
                records={projects}
                fetching={projectsQ.isLoading}
                minHeight={120}
                noRecordsText="No projects assigned to this company"
                columns={[
                  {
                    accessor: 'name',
                    title: 'Project',
                    render: (p) => (
                      <Anchor component={Link} to={`/projects/${p.id}`}>
                        {p.name}
                      </Anchor>
                    ),
                  },
                  { accessor: 'tract_count', title: 'Tracts', width: 100 },
                  {
                    accessor: 'updated_at',
                    title: 'Updated',
                    width: 160,
                    render: (p) => dayjs(p.updated_at).format('YYYY-MM-DD HH:mm'),
                  },
                ]}
              />
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="viewers" pt="md">
            <Stack gap="md">
              <Card withBorder padding="md">
                <Stack gap="sm">
                  <Title order={5}>Grant access</Title>
                  <Text size="xs" c="dimmed">
                    Search for a user. Granting only unlocks the rollup metrics; it does not
                    confer any project-scoped permissions.
                  </Text>
                  <TextInput
                    placeholder="Search by username or email"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.currentTarget.value)}
                  />
                  {userSearch && usersQ.data && (
                    <Stack gap={4}>
                      {usersQ.data.users.map((u) => {
                        const already = viewers.some((v) => v.user_id === u.id);
                        return (
                          <Group key={u.id} justify="space-between">
                            <Text size="sm">
                              <strong>{u.username}</strong> · {u.email}
                            </Text>
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconUserPlus size={12} />}
                              disabled={already}
                              onClick={() => confirmGrant(u.id, `${u.username} (${u.email})`)}
                            >
                              {already ? 'Already a viewer' : 'Grant'}
                            </Button>
                          </Group>
                        );
                      })}
                      {usersQ.data.users.length === 0 && (
                        <Text size="sm" c="dimmed">
                          No matches.
                        </Text>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Card>

              <Paper withBorder radius="md">
                <DataTable
                  records={viewers}
                  fetching={viewersQ.isLoading}
                  minHeight={120}
                  noRecordsText="No viewers yet"
                  columns={[
                    {
                      accessor: 'user_username',
                      title: 'User',
                      render: (v) => (
                        <Stack gap={0}>
                          <Text size="sm">{v.user_username}</Text>
                          <Text size="xs" c="dimmed">
                            {v.user_email}
                          </Text>
                        </Stack>
                      ),
                    },
                    {
                      accessor: 'granted_at',
                      title: 'Granted',
                      width: 160,
                      render: (v) => dayjs(v.granted_at).format('YYYY-MM-DD'),
                    },
                    {
                      accessor: 'actions',
                      title: '',
                      width: 60,
                      render: (v) => (
                        <Tooltip label="Revoke">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => confirmRevoke(v.user_id, v.user_username)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </Paper>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {isDeleted && (
          <Alert color="red" variant="light" title="This company is soft-deleted">
            The rollup endpoint may still return values from before the delete; the row is hidden
            from the companies list.
          </Alert>
        )}
      </Stack>
    </Container>
  );
}

function RollupCard({ label, value }: { label: string; value: string }) {
  return (
    <Card withBorder padding="md">
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="xl" fw={600}>
        {value}
      </Text>
    </Card>
  );
}
